import { createSlice, createAction } from "@reduxjs/toolkit";
import { RootState, dispatch } from "store/store";
import {
	ConflictedResponse,
	Merchant,
	MerchantQueries,
	MerchantSaleReport,
	MerchantStatus,
	ResponseMerchant,
	SubscriptionPackage,
} from "../types";
import { ApiErrorResponse, ApiResponse, USER_TYPE } from "types";
import { Car } from "features/cars/types";
import { BaseQuery, CarQuery } from "service/queries";
import { getCarsApi, getMerchantSaleReportAPI } from "service/apis";
import { CAR_STATUS_TYPE } from "constants/constants";
import { CarPart, CarPartQueryParams } from "../tabs/carParts/type";

interface MerchantState {
	merchantList: Merchant[];
	merchantDetail: ResponseMerchant;
	merchantTotal: number;
	merchantCars: Car[];
	merchantCarTotal: number;
	merchantSaleReport: MerchantSaleReport | null;
	merchantCarPartList: CarPart[];
	merchantCarPartDetail: CarPart;
	merchantCarPartTotal: number;
	subscriptionPackages: SubscriptionPackage[];
}

export const merchantDefault: ResponseMerchant = {
	id: 0,
	username: "",
	fullName: "",
	avatar: "",
	phoneNumber: "",
	email: "",
	idCard: "",
	gender: 1,
	birthday: "",
	status: MerchantStatus.DISABLE,
	address: "",
	title: "",
	contract: "",
	contractDate: "",
	userType: USER_TYPE.MERCHANT,
	isRemoved: false,
};

export const carPartDefault: CarPart = {
	attribute: {
		isInstallationSupport: false,
		partType: {
			attribute: {
				name: "",
			},
		},
		state: "",
	},
	_id: "",
	name: "",
	price: 0,
	status: "",
	video: "",
	photos: [],
};

const initialState: MerchantState = {
	merchantList: [],
	merchantDetail: merchantDefault,
	merchantTotal: 0,
	merchantCars: [],
	merchantCarTotal: 0,
	merchantSaleReport: null,
	merchantCarPartList: [],
	merchantCarPartDetail: carPartDefault,
	merchantCarPartTotal: 0,
	subscriptionPackages: [],
};

export const merchantSlice = createSlice({
	name: "merchant",
	initialState,
	reducers: {
		setListMerchant: (state, action) => {
			state.merchantList = action.payload.data;
			state.merchantTotal = action.payload.total;
		},
		setDetailMerchant: (state, action) => {
			state.merchantList = state.merchantList.map(merchant => {
				if (merchant.id === action.payload.data.id) {
					return action.payload.data;
				}
				return merchant;
			});

			state.merchantDetail = action.payload.data;
		},
		resetDetailMerchant: state => {
			state.merchantDetail = merchantDefault;
		},
		changeStatusMerchant: (state, action) => {
			const { id, status } = action.payload;
			const index = state.merchantList.findIndex(el => el.id === id);
			state.merchantList[index].status = status;
		},
		setMerchantCars: (state, action) => {
			state.merchantCars = action.payload.data;
			state.merchantCarTotal = action.payload.total;
		},
		setMerchantSaleReport: (state, action) => {
			state.merchantSaleReport = action.payload.data;
		},
		setMerchantCarPartList: (state, action) => {
			state.merchantCarPartList = action.payload.data;
			state.merchantCarPartTotal = action.payload.total;
		},
		setMerchantCarPartDetail: (state, action) => {
			state.merchantCarPartDetail = action.payload.data;
		},
		changeStatusMerchantCarPart: (state, action) => {
			state.merchantCarPartDetail.status = action.payload.status;
		},
		setSubscriptionPakages: (state, action) => {
			state.subscriptionPackages = action.payload;
		},
	},
});

export const selectMerchantList = (state: RootState) =>
	state.merchant.merchantList;
export const selectMerchantTotal = (state: RootState) =>
	state.merchant.merchantTotal;
export const selectMerchantDetail = (state: RootState) =>
	state.merchant.merchantDetail;
export const selectMerchantCars = (state: RootState) =>
	state.merchant.merchantCars;
export const selectMerchantCarTotal = (state: RootState) =>
	state.merchant.merchantCarTotal;
export const selectMerchantSaleReport = (state: RootState) =>
	state.merchant.merchantSaleReport;
export const selectMerchantCarPartList = (state: RootState) =>
	state.merchant.merchantCarPartList;
export const selectMerchantCarPartDetail = (state: RootState) =>
	state.merchant.merchantCarPartDetail;
export const selectMerchantCarPartTotal = (state: RootState) =>
	state.merchant.merchantCarPartTotal;
export const selectSubscriptionPakages = (state: RootState) =>
	state.merchant.subscriptionPackages;

export const {
	setListMerchant,
	setDetailMerchant,
	resetDetailMerchant,
	changeStatusMerchant,
	setMerchantCars,
	setMerchantSaleReport,
	setMerchantCarPartList,
	setMerchantCarPartDetail,
	changeStatusMerchantCarPart,
	setSubscriptionPakages,
} = merchantSlice.actions;

export interface GetListMerchantAsyncPayload {
	query: MerchantQueries;
}
export const getListMerchantAsync = createAction(
	"merchant/getListMerchantAsync",
	(payload: GetListMerchantAsyncPayload) => ({
		payload: payload,
	}),
);

export interface FilterListMerchantAsyncPayload {
	query: MerchantQueries;
	callback?: () => void;
}
export const filterListMerchantAsync = createAction(
	"merchant/filterListMerchantAsync",
	(payload: FilterListMerchantAsyncPayload) => ({
		payload: payload,
	}),
);

export interface GetDetailMerchantPayLoad {
	id: string;
}
export const getDetailMerchantAsync = createAction(
	"merchant/getDetailMerchantAsync",
	(payload: GetDetailMerchantPayLoad) => ({
		payload,
	}),
);

export interface CreateMerchantPayLoad {
	data: Merchant;
	returnStatus: (status: boolean) => void;
}
export const createMerchantAsync = createAction(
	"merchant/createMerchantAsync",
	(payload: CreateMerchantPayLoad) => ({
		payload,
	}),
);

export interface UpdateMerchantPayLoad {
	data: Merchant;
	id: string;
	callback?: () => void;
	failtureCallback?: (error: ApiErrorResponse<ConflictedResponse>) => void;
}
export const updateMerchantAsync = createAction(
	"merchant/updateMerchantAsync",
	(payload: UpdateMerchantPayLoad) => ({
		payload,
	}),
);

export interface GetListCarPartPayload {
	id?: string;
	query: CarPartQueryParams;
	callback: () => void;
}

export const getListCarPartAsync = createAction(
	"merchant/getListCarPartAsync",
	(payload: GetListCarPartPayload) => ({
		payload,
	}),
);

export interface GetDetailCarPartPayload {
	merchantId?: string;
	carPartId: string;
	callback: () => void;
}

export const getDetailCarPartAsync = createAction(
	"merchant/getDetailCarPartAsync",
	(payload: GetDetailCarPartPayload) => ({
		payload,
	}),
);

export interface UpdateStatusCarPartPayload {
	merchantId?: string;
	carPartId: string;
	data: {
		status: string;
	};
	callback: () => void;
}

export const updateStatusCarPartAsync = createAction(
	"merchant/updateStatusCarPartAsync",
	(payload: UpdateStatusCarPartPayload) => ({
		payload,
	}),
);

export interface GetSubscriptionPakagesPayload {
	callback?: () => void;
}

export const getSubscriptionPakagesAsync = createAction(
	"merchant/getSubscriptionPakagesAsync",
	(payload: GetSubscriptionPakagesPayload) => ({
		payload,
	}),
);

export const filterMerchantCars = (
	query: CarQuery,
	callback?: (error: string | string[]) => void,
) => {
	if (!query.merchant) return;
	query.merchant = Number.parseInt(query.merchant.toString());
	getCarsApi(query)
		.then((result: ApiResponse<Car[]>) => {
			result?.data?.map(ele => {
				if (ele.status === CAR_STATUS_TYPE.SOLD_BY_MERCHANT) {
					ele.status = CAR_STATUS_TYPE.SOLD_BY_MERCHANT.replaceAll("_", " ");
				}
			});
			dispatch(setMerchantCars(result));
		})
		.catch(error => {
			callback && callback(error);
		});
};

export const getMerchantSaleReport = (
	id: string,
	callback?: (error: string | string[]) => void,
) => {
	getMerchantSaleReportAPI(id)
		.then((result: ApiResponse<MerchantSaleReport>) => {
			dispatch(setMerchantSaleReport(result));
		})
		.catch(error => {
			callback && callback(error);
		});
};

export default merchantSlice.reducer;
